<template>
  <div>
    <div v-if="!studentScheduleItems || studentScheduleItems.length < 1">
      No student schedule found
    </div>

    <div v-else>
      <div class="d-flex">
        <v-btn class="my-auto mr-2" icon @click="prevDate">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-select
          @change="initData"
          v-model="selectedDate"
          :items="dates"
          label="Select a date"
        >
          <template v-slot:selection="data">
            {{ data.item | formatDate }}
          </template>
          <template v-slot:item="data">
            {{ data.item | formatDate }}
          </template>
        </v-select>
        <v-btn class="my-auto ml-2" icon @click="nextDate">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <div class="d-flex justify-space-between mb-2 flex-column flex-sm-row">
        <!-- Color Legend -->
        <div class="color-legend">
          <div
            class="legend-item"
            @click="toggleLegendFilter('unmarked')"
            :class="{ 'text-decoration-line-through': legendFilters.unmarked }"
          >
            <div class="color-box unmarked"></div>
            Unmarked ({{ totalUnmarked }})
          </div>
          <div
            class="legend-item"
            @click="toggleLegendFilter('present')"
            :class="{ 'text-decoration-line-through': legendFilters.present }"
          >
            <div class="color-box present"></div>
            Present ({{ totalPresent }})
          </div>
          <div
            class="legend-item"
            @click="toggleLegendFilter('absent')"
            :class="{ 'text-decoration-line-through': legendFilters.absent }"
          >
            <div class="color-box absent"></div>
            Absent ({{ totalAbsent }})
          </div>
        </div>

        <v-btn
          color="primary"
          :disabled="isFutureDate"
          @click="saveAttendance"
          :loading="loading"
        >
          Save Attendance
        </v-btn>
      </div>
      <p class="text-caption">
        Instruction: Click on student name to cycle through unmarked, present
        and absent.
      </p>

      <div class="d-flex justify-space-between mb-4">
        <v-text-field
          hide-details
          v-model="filter"
          placeholder="Filter by student name"
          clearable
          solo
        ></v-text-field>

        <attendance-markers @on-mark="markAll" />
      </div>

      <div style="height: 500px; max-height: 500px; overflow: auto">
        <div
          class="my-4"
          v-for="item in filteredStudentSchedules"
          :key="item.id"
        >
          <div class="d-flex justify-space-between">
            <h4>
              {{ item.classroomId ? item.classroomName : item.teacherName }}
            </h4>

            <attendance-markers
              v-if="item.classroomId && item.details && item.details.length > 4"
              @on-mark="(eventArg) => markAll(eventArg, item.classroomId)"
              :showLabel="false"
            />
          </div>
          <v-divider />
          <v-row class="mt-1">
            <v-col
              cols="12"
              :sm="6"
              :md="4"
              v-for="studentSchedule in item.details"
              :key="studentSchedule.id"
            >
              <div
                @click="toggleAttendance(studentSchedule)"
                class="pa-1 rounded student-cell"
                :class="{
                  unmarked: studentSchedule.state === 'unmarked',
                  present: studentSchedule.state === 'present',
                  absent: studentSchedule.state === 'absent',
                }"
              >
                <div v-if="studentSchedule.state !== 'unmarked'">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        class="status-icon"
                        icon
                      >
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="openNotes(studentSchedule)">
                        <v-list-item-title>
                          <span>Add Notes</span>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="studentSchedule.state === 'present'"
                        disabled
                        @click="console.log('UE selected')"
                      >
                        <v-list-item-title>
                          <span>Mark as Late</span>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="studentSchedule.state === 'present'"
                        disabled
                        @click="console.log('E selected')"
                      >
                        <v-list-item-title>
                          <span>Mark as Left Early</span>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="studentSchedule.state === 'absent'"
                        @click="toggleExcusedAbsent(studentSchedule.id)"
                      >
                        <v-list-item-title>
                          <span>
                            {{
                              isExcusedAbsent(studentSchedule.id)
                                ? "Mark as Unexcused"
                                : "Mark as Excused"
                            }}
                          </span>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
                <div class="long-name">
                  {{ studentSchedule.studentFirstName }}
                  {{ studentSchedule.studentLastName }}
                </div>

                <div class="text-caption">
                  {{ getProgramName(studentSchedule.programId) }}
                </div>
                <!-- Make the icon an absolute element, and set it to top-right -->
                <div class="text-caption">
                  {{ studentSchedule.startTime | formatTime }} -
                  {{ studentSchedule.endTime | formatTime }}
                </div>

                <div>
                  <span v-if="studentSchedule.state === 'absent'">
                    {{
                      isExcusedAbsent(studentSchedule.id)
                        ? "Excused Absent"
                        : ""
                    }}
                  </span>
                </div>

                <!-- Notes Icon -->
                <div v-if="studentSchedule.notes">
                  <v-btn
                    class="notes-icon"
                    icon
                    @click.stop="openNotes(studentSchedule)"
                  >
                    <v-icon small> mdi-note-outline </v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Bottom sheet for notes -->
      <v-bottom-sheet v-model="selectedStudent.visible" inset>
        <v-card>
          <v-card-subtitle>
            Notes for {{ selectedStudent.firstName }}
            {{ selectedStudent.lastName }}
          </v-card-subtitle>
          <v-card-text>
            <v-textarea
              v-model="selectedStudent.notes"
              label="Notes"
              auto-grow
              outlined
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn color="primary" @click="saveNotes">Save</v-btn>
            <v-btn @click="selectedStudent.visible = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-bottom-sheet>
    </div>
  </div>
</template>

<script>
import dialogMixin from "@/components/schedule/dialogMixin.js";
import AttendanceService from "@/services/attendanceService.js";
import moment from "moment";
import { mapActions } from "vuex";
import AttendanceMarkers from "@/components/attendance/AttendanceMarkers.vue";
export default {
  mixins: [dialogMixin],
  components: { AttendanceMarkers },
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      filter: "",
      localStudentSchedules: [],
      selectedStudent: {
        visible: false,
        notes: "",
        firstName: "",
        lastName: "",
        studentSchedule: null,
      },
      programId2Name: {},
      legendFilters: {
        unmarked: false,
        present: false,
        absent: false,
      },
      loading: false,
      excusedAbsents: [],
      selectedDate: null,
      studentScheduleItems: [],
    };
  },
  watch: {
    items: {
      deep: true, // Enables deep watching
      handler(newVal) {
        this.studentScheduleItems = newVal;
        this.selectedDate = this.dates
          ? this.dates[0]
          : moment().format("YYYY-MM-DD");
        this.initData();
      },
    },
  },
  computed: {
    filteredStudentSchedules() {
      if (
        !this.filter &&
        !this.legendFilters.unmarked &&
        !this.legendFilters.present &&
        !this.legendFilters.absent
      ) {
        return this.localStudentSchedules;
      }

      const filterLowerCase = this.filter.toLowerCase();
      return this.localStudentSchedules.map((item) => {
        return {
          ...item,
          details: item.details.filter((studentSchedule) => {
            const nameMatches = (
              studentSchedule.studentFirstName +
              " " +
              studentSchedule.studentLastName
            )
              .toLowerCase()
              .includes(filterLowerCase);

            const attendanceMatches =
              (!this.legendFilters.present &&
                studentSchedule.state.toLowerCase() === "present") ||
              (!this.legendFilters.unmarked &&
                studentSchedule.state.toLowerCase() === "unmarked") ||
              (!this.legendFilters.absent &&
                studentSchedule.state.toLowerCase() === "absent");

            return nameMatches && attendanceMatches;
          }),
        };
      });
    },
    totalUnmarked() {
      let count = 0;
      this.localStudentSchedules.forEach((item) =>
        item.details.forEach((studentSchedule) => {
          if (studentSchedule.state.toLowerCase() === "unmarked") count++;
        })
      );
      return count;
    },
    totalPresent() {
      let count = 0;
      this.localStudentSchedules.forEach((item) =>
        item.details.forEach((studentSchedule) => {
          if (studentSchedule.state.toLowerCase() === "present") count++;
        })
      );
      return count;
    },
    totalAbsent() {
      let count = 0;
      this.localStudentSchedules.forEach((item) =>
        item.details.forEach((studentSchedule) => {
          if (studentSchedule.state.toLowerCase() === "absent") count++;
        })
      );
      return count;
    },
    dates() {
      let dateArray = [];
      let currentDate = moment(this.dateRange.start);

      while (currentDate <= moment(this.dateRange.end)) {
        dateArray.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, "days");
      }

      return dateArray;
    },
    isFutureDate() {
      const tomorrow = moment().add(1, "days").startOf("day");

      if (
        this.selectedDate &&
        moment(this.selectedDate).isSameOrAfter(tomorrow)
      ) {
        // selected date is in the future
        return true;
      } else {
        // selected date is not in the future
        return false;
      }
    },
  },
  created() {
    this.studentScheduleItems = this.items;
    this.localStudentSchedules = JSON.parse(
      JSON.stringify(this.studentScheduleItems)
    );

    //init selected date
    this.selectedDate = this.dates
      ? this.dates[0]
      : moment().format("YYYY-MM-DD");

    this.initData();
    this.loadPrograms();
  },
  methods: {
    ...mapActions(["addErrorMessage", "addMessage", "addSuccessMessage", "clearMessage"]),

    toggleAttendance(studentSchedule) {
      if (studentSchedule.state.toLowerCase() === "unmarked") {
        studentSchedule.state = "present";
      } else if (studentSchedule.state.toLowerCase() === "present") {
        studentSchedule.state = "absent";
      } else if (studentSchedule.state.toLowerCase() === "absent") {
        studentSchedule.absentExcused = null;
        studentSchedule.state = "unmarked";
      }
    },
    toggleExcusedAbsent(scheduleId) {
      const index = this.excusedAbsents.findIndex((a) => a == scheduleId);
      if (index >= 0) {
        this.excusedAbsents.splice(index, 1);
      } else {
        this.excusedAbsents.push(scheduleId);
      }
    },
    isExcusedAbsent(scheduleId) {
      return this.excusedAbsents.findIndex((a) => a == scheduleId) >= 0;
    },
    markAll(state, classroomId = null) {
      this.localStudentSchedules.forEach((item) =>
        item.details
          .filter(
            (studentSchedule) =>
              classroomId == null || studentSchedule.classroomId == classroomId
          )
          .forEach((studentSchedule) => {
            studentSchedule.state = state;
          })
      );
    },
    toggleLegendFilter(filter) {
      this.legendFilters[filter] = !this.legendFilters[filter];
    },
    openNotes(studentSchedule) {
      this.selectedStudent.visible = true;
      this.selectedStudent.firstName = studentSchedule.studentFirstName;
      this.selectedStudent.lastName = studentSchedule.studentLastName;
      this.selectedStudent.notes = studentSchedule.notes || "";
      this.selectedStudent.studentSchedule = studentSchedule;
    },
    saveNotes() {
      this.selectedStudent.studentSchedule.notes = this.selectedStudent.notes;
      this.selectedStudent.visible = false;
    },
    loadPrograms(center) {
      const params = { centerId: center };
      this.$axios.get("/program/get-by-center", { params }).then((resp) => {
        this.programId2Name = resp.data.reduce((acc, p) => {
          acc[p.id] = p.name;
          return acc;
        }, {});
      });
    },
    getProgramName(id) {
      return this.programId2Name[id];
    },
    async updateStudentSchedules() {
      try {
        // Get All Attendances
        const studentIds = null;
        const programIds = null;

        const startDate = this.selectedDate;
        const endDate = this.selectedDate;

        // Fetch attendances
        let attendances = await AttendanceService.searchAttendances(
          studentIds,
          programIds,
          startDate,
          endDate
        );

        // console.log(Object.values(this.localStudentSchedules));
        // console.log(Object.values(attendances));

        // Loop over attendances and update localStudentSchedules
        for (let attendance of attendances) {
          // console.log(attendance);
          // Find the corresponding studentSchedule in localStudentSchedules
          for (let item of Object.values(this.localStudentSchedules)) {
            for (let studentSchedule of item.details) {
              if (
                studentSchedule.id == attendance.studentScheduleId &&
                moment(studentSchedule.startTime).isSame(
                  moment(attendance.attendedDate),
                  "day"
                )
              ) {
                // Update the state and notes of studentSchedule
                let state = attendance.status;
                let lowerCaseState = state.toLowerCase();

                switch (lowerCaseState) {
                  case "excused":
                    this.excusedAbsents.push(studentSchedule.id);
                    state = "absent";
                    break;
                  case "unexcused":
                    state = "absent";
                    break;
                  default:
                  // Handle other cases as needed
                }
                studentSchedule.state = state.toLowerCase();
                studentSchedule.notes = attendance.notes;
                studentSchedule.attendanceId = attendance.id; // Assuming the attendance object has an id field
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error(
          "An error occurred while fetching attendance data:",
          error
        );
      }
    },
    async saveAttendance() {
      this.loading = true;

      // Prepare attendance list to be saved
      let attendances = [];

      // Loop over all localStudentSchedules
      this.localStudentSchedules.forEach((item) => {
        item.details.forEach((studentSchedule) => {
          // Translate our local "state" to server's "status" terminology
          let status = studentSchedule.state;

          // Skip unmarked if its not already saved
          if (status == "unmarked" && !studentSchedule.attendanceId) return;

          const attendedDate = moment(studentSchedule.startTime).format(
            "YYYY-MM-DD"
          );

          attendances.push({
            attendanceId: studentSchedule.attendanceId,
            scheduleId: studentSchedule.id,
            programId: studentSchedule.programId,
            teacherId: studentSchedule.teacherId,
            status: status.toLowerCase(),
            note: studentSchedule.notes || "", // Assuming notes is optional
            attendedDate: attendedDate,
            absentExcused: this.isExcusedAbsent(studentSchedule.id)
              ? true
              : false,
          });
        });
      });
      // Call AttendanceService to save the data
      AttendanceService.saveAllAttendances(attendances)
        .then((resp) => {
          //resp is a 2d array; first dimension is date and 2nd dimension is saved attendance
          if(!resp) {
            this.addMessage('No attendance saved')
            return;
          }

          const savedAttendances = [].concat(...resp);

          // Initially set all attendanceIds to null
          for (let item of this.localStudentSchedules) {
            for (let studentSchedule of item.details) {
              studentSchedule.attendanceId = null;
            }
          }
          // Assign saved attendance id to schedule
          for (let attendance of savedAttendances) {
            for (let item of Object.values(this.localStudentSchedules)) {
              for (let studentSchedule of item.details) {
                if (
                  studentSchedule.id == attendance.studentScheduleId && 
                  studentSchedule.startTime.startsWith(attendance.attendedDate)
                ) {
                  studentSchedule.attendanceId = attendance.id;
                }
              }
            }
          }

          this.addSuccessMessage("Attendance updated");
        })
        .catch((error) => {
          this.addErrorMessage(
            "Error saving attendance" || error.data.messsage
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    filterStudentScheduleItemsForSelectedDate(date) {
      const startTime = date
        ? moment(date).startOf("day")
        : moment().startOf("day");
      const endTime = date ? moment(date).endOf("day") : moment().endOf("day");
      return this.studentScheduleItems.filter((item) => {
        if (item.type != "student-schedule") {
          return false;
        }

        const firstStudentSchedule = item.details[0];
        const itemStartTime = moment(firstStudentSchedule.startTime);
        const itemEndTime = moment(firstStudentSchedule.endTime);
        const withinDateRange =
          itemStartTime.isSameOrAfter(startTime) &&
          itemEndTime.isSameOrBefore(endTime);
        return withinDateRange;
      });
    },
    initData() {
      // filter studentScheduleItems for selected date.
      const workingScheduleItems =
        this.filterStudentScheduleItemsForSelectedDate(this.selectedDate);
      // Deep copy the new value
      this.localStudentSchedules = JSON.parse(
        JSON.stringify(workingScheduleItems)
      );
      // Reinitialize attendance and sort by startTime
      this.localStudentSchedules.forEach((item) => {
        item.details.sort((a, b) => a.startTime.localeCompare(b.startTime));
        item.details.forEach((studentSchedule) => {
          this.$set(studentSchedule, "state", "unmarked");
        });
      });

      this.updateStudentSchedules();
    },
    prevDate() {
      const currentIndex = this.dates.indexOf(this.selectedDate);
      if (currentIndex > 0) {
        this.selectedDate = this.dates[currentIndex - 1];
      }
      this.initData();
    },
    nextDate() {
      const currentIndex = this.dates.indexOf(this.selectedDate);
      if (currentIndex < this.dates.length - 1) {
        this.selectedDate = this.dates[currentIndex + 1];
      }
      this.initData();
    },
  },
};
</script>

<style>
.status-icon {
  position: absolute;
  top: 0;
  right: 0;
}
.notes-icon {
  position: absolute;
  bottom: 0;
  right: 0;
}
.color-legend {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.color-box {
  width: 15px;
  height: 15px;
  margin-right: 5px;
}

.unmarked {
  border: 1px solid #ccc;
  color: #666;
}

.present {
  background-color: #90ee90; /* Light Green */
}

.absent {
  background-color: #ff7f7f; /* Light Red */
}

.student-cell {
  height: 95px;
  position: relative;
  background-color: #90ee90; /* Light Green */
  cursor: pointer;
  position: relative; /* Position relative for notes-icon */
}

.student-cell.absent {
  background-color: #ff7f7f; /* Light Red */
}

.student-cell.unmarked {
  background-color: #fff;
}

.long-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Add any desired width or max-width property here */
}
</style>
